// DTOs da feature Pesquisa de Preços — espelham os schemas Pydantic do backend
// price-research (app/schemas/price_search.py). Apenas o subconjunto consumido pelo front.

// Teto de 48 meses (20/08/2026): objeto específico não tem contratação recente.
// O backend ainda AMPLIA sozinho (48 → 60) quando a janela pedida rende pouco.
export const PERIODOS_MESES = [6, 12, 18, 24, 36, 48] as const
export type PeriodoMeses = (typeof PERIODOS_MESES)[number]

// Status possíveis de uma pesquisa (app/repo/models.py :: StatusPesquisa)
export type StatusPesquisa =
    | 'PENDENTE'
    | 'PARSEANDO'
    | 'COLETANDO'
    | 'AGUARDANDO_LLM'
    | 'CALCULANDO'
    | 'GERANDO_RELATORIO'
    | 'CONCLUIDO'
    | 'ERRO'

export const STATUS_FINAIS: StatusPesquisa[] = ['CONCLUIDO', 'ERRO']

export interface ItemInput {
    descricao: string // min 5 chars
    quantidade: number // > 0
    unidade: string
    catmat_catser?: string | null
    marca_referencia?: string | null
    especificacoes?: string | null
}

export interface PriceSearchCreate {
    modo_entrada?: string // default "MANUAL_MULTIPLO"
    items: ItemInput[] // 1..10
    periodo_meses: number // 6 | 12 | 18 | 24
    // Escopo de fontes: 'TODAS' (default) | 'BASES_PUBLICAS' | 'VAREJO_WEB'.
    // VAREJO_WEB restringe ao Parâmetro III (Google) — o backend registra
    // alerta de justificativa (art. 5º, §1º) no relatório.
    fontes_escopo?: string
    uf?: string | null // 2 chars
    observacoes?: string | null
    // Identificação do processo (art. 3º, I/II da IN 65/2021)
    processo?: string | null
    orgao?: string | null
    responsavel?: string | null
    // Vínculo de origem entre módulos (handoff): módulo que originou a pesquisa
    // (ex.: 'carona' = Ata Inteligente) e o id do registro de origem (ex.: a
    // justificativa). Persistidos pelo backend e devolvidos no Out/Detail.
    origem_modulo?: string | null
    origem_ref_id?: string | null
    // Anexos de insumo de preço já enviados (POST /pesquisas/anexos). Vão no create,
    // e não em chamada posterior, porque a coleta começa assim que o create responde —
    // anexar depois perderia a corrida contra o primeiro item coletado.
    anexo_ids?: string[]
}

// Um preço lido de um PDF que o responsável enviou. NÃO entra na cesta automática:
// a fonte não foi consultada por esta pesquisa, então quem inclui é o gestor, na
// revisão, assumindo a autoria.
export interface PrecoDoDocumento {
    valor: number
    descricao: string
    data?: string | null
    pagina?: number | null
    trecho?: string | null
    fonte_declarada?: string | null
    parametro_in65?: string | null
}

export interface PrecoRecusadoDoDocumento extends PrecoDoDocumento {
    motivo: string
    motivo_rotulo?: string | null
}

export interface LeituraDoAnexo {
    linhas_lidas: number
    disponiveis_para_inclusao: PrecoDoDocumento[]
    recusados: PrecoRecusadoDoDocumento[]
}

export interface AnexoOut {
    id: string
    nome_arquivo: string
    paginas: number
    // OK | SEM_TEXTO | FALHOU — SEM_TEXTO é o PDF escaneado, o caso mais comum e o
    // mais confuso quando o sistema fica calado.
    status_extracao: string
    sha256: string
    arquivo_guardado: boolean
    mensagem: string
    leitura?: LeituraDoAnexo | null
    // PENDENTE = a leitura por IA foi enfileirada e o resultado chega por push do
    // notifier. FALHOU precisa ser visível: sem isso o card giraria para sempre.
    ia_status?: 'PENDENTE' | 'CONCLUIDA' | 'FALHOU' | 'NAO_APLICAVEL'
}

export interface AnexosResposta {
    anexos: AnexoOut[]
    aviso: string
}

export interface PriceSearchOut {
    id: string
    status: StatusPesquisa
    current_step: number
    periodo_meses: number
    fontes_escopo?: string
    objeto_geral?: string | null
    uf?: string | null
    observacoes?: string | null
    processo?: string | null
    orgao?: string | null
    responsavel?: string | null
    valor_total_estimado?: number | null
    itens_total: number
    itens_concluidos: number
    error_message?: string | null
    // Vínculo de origem (null/ausente em pesquisa avulsa) — ver PriceSearchCreate.
    origem_modulo?: string | null
    origem_ref_id?: string | null
    created_at: string
    updated_at: string
    finished_at?: string | null
}

export interface EventoOut {
    etapa: string
    status: string
    detalhe?: string | null
    timestamp: string
}

// Equalização de um preço coletado (padronização para a unidade-base de comparação).
// Campos entregues pelo backend price-research junto do preço; todos opcionais porque
// o deploy é assíncrono e uma corrida pode devolver o preço sem o bloco completo.
export interface EqualizacaoResultado {
    unidade_base?: string | null // unidade BASE (ex.: "un", "kg", "L")
    fator_embalagem?: number | null // fator aplicado (ex.: 1000 p/ pacote c/ 1000)
    base_aplicada?: string | null // prosa pronta p/ tooltip (ex.: "R$ 0,0500/un — de pacote c/ 1000")
    base_confirmada?: boolean | null // false + UTILIZADO = base assumida sem confirmação
    confiabilidade?: 'ALTA' | 'MEDIA' | 'BAIXA' | string | null
    foi_equalizado?: boolean | null // se ausente, derivar de fator/valores (ver montarItens)
    premissas?: string[] | null // texto de apoio p/ tooltip
    riscos?: string[] | null // texto de apoio p/ tooltip
}

// Um preço coletado dentro de analise_por_item[].precos[]. Subconjunto consumido pela
// tela de revisão da cesta (transparência da equalização).
export interface PrecoResultado {
    id_coleta: number
    status_utilizacao?: string | null // 'UTILIZADO' | 'DESCARTADO'
    motivo_descarte?: string | null
    origem?: string | null // 'PNCP' | 'WEB' | ...
    parametro_in65?: string | null
    orgao_contratante?: string | null
    fornecedor_nome?: string | null
    descricao_item_encontrado?: string | null
    link?: string | null
    valor_unitario?: number | null // VALOR DE ORIGEM (bruto)
    unidade?: string | null // UNIDADE DE ORIGEM (ex.: "DÚZIA", "UNIDADE", "KG")
    valor_unitario_equalizado?: number | null // valor EQUALIZADO
    equalizacao?: EqualizacaoResultado | null
    // Presente só em preço de origem DOCUMENTO: de qual arquivo/página/trecho o número
    // saiu. É o que permite à revisão e ao relatório dizer a procedência em vez de
    // exibir o preço como se tivesse sido coletado por nós.
    proveniencia?: {
        anexo_id?: string | null
        arquivo_nome?: string | null
        pagina?: number | null
        trecho_literal?: string | null
        fonte_declarada?: string | null
        sha256?: string | null
    } | null
}

// resultado é o ResultadoConsolidado (JSONB) — estrutura aninhada e em evolução no
// backend; tipamos de forma defensiva (Record) e renderizamos campos opcionais com
// optional chaining + fallback de JSON cru.
export interface EstimativaTempo {
    base: 'historico' | 'equalizacao'
    min?: number | null
    max?: number | null
    acima_do_usual?: boolean
}

export interface PriceSearchDetail extends PriceSearchOut {
    resultado?: Record<string, any> | null
    eventos: EventoOut[]
    estimativa?: EstimativaTempo | null
}

// Farol do PNCP (GET /pesquisas/pncp-status): pior status entre o portal e a API.
export interface PncpStatus {
    status: 'OK' | 'SLOW' | 'DEGRADED' | 'DOWN' | 'UNKNOWN'
    portais: {nome: string; status: string}[]
    atualizado_em?: string | null
    url: string
}

export interface PriceSearchList {
    total: number
    pesquisas: PriceSearchOut[]
}

// Revisão da cesta (Art. 6º / Parâmetro IV)
export interface RevisaoAjuste {
    numero: number
    id_coleta: number
    status: 'UTILIZADO' | 'DESCARTADO'
    valor_unitario_equalizado?: number
}
export interface RevisaoNova {
    numero: number
    fornecedor_nome: string
    valor_unitario_equalizado: number
    link?: string
    descricao_item_encontrado?: string
}
export interface RevisaoMetodo {
    numero: number
    metodo: 'AUTO' | 'MEDIA' | 'MEDIANA' | 'MENOR'
    justificativa?: string
}
export interface RevisaoPayload {
    ajustes: RevisaoAjuste[]
    novos: RevisaoNova[]
    metodos: RevisaoMetodo[]
}

// Assistente "✨ preencher com IA" — usado por PriceResearchApiService e por
// InexigibilidadeApiService (mesmo backend/contrato /assistente). Endpoint
// assíncrono: o POST devolve {id, status} de imediato (202); os campos de
// resultado só vêm preenchidos quando status === 'CONCLUIDO' (ou erro quando
// status === 'ERRO').
export interface AssistenteOut {
    id: string
    status: string // PENDENTE | CONCLUIDO | ERRO
    texto?: string | null
    especificacoes?: string | null
    catmat?: string | null
    itens?: string[] | null
    itens_melhorados?: {indice: number; descricao_sugerida: string; motivo?: string}[] | null
    erro?: string | null
}
